import { useEffect, useRef } from 'react';

import { SSE } from 'sse.js';

import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface BaseExportEvent {
  type: string;
  jobId: string;
  jobType: string;
  progress?: number;
  totalDocuments?: number;
  processedDocuments?: number;
  error?: string;
  timestamp?: number;
}

export interface UseExportEventsConfig<TEvent extends BaseExportEvent> {
  progressEndpoint: string;
  enabled?: boolean;
  onEvent: (event: TEvent) => void;
}

export const useExportEvents = <TEvent extends BaseExportEvent>({
  progressEndpoint,
  onEvent,
  enabled = true,
}: UseExportEventsConfig<TEvent>) => {
  const sseRef = useRef<SSE | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();

  useEffect(() => {
    if (!enabled || !progressEndpoint) {
      return;
    }

    let isMounted = true;

    const connectToSSE = () => {
      try {
        const eventSource = new SSE(progressEndpoint, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          if (!isMounted) return;

          try {
            if (!event.data || !event.data.trim()) {
              return;
            }

            const data: TEvent = JSON.parse(event.data);
            onEvent(data);
          } catch (error) {
            console.error('Failed to parse export event:', error);
          }
        };

        eventSource.onerror = (error: unknown) => {
          console.error('Export SSE error:', JSON.stringify(error));
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Failed to setup export event source:', JSON.stringify(error));
      }
    };

    connectToSSE();

    return () => {
      isMounted = false;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [progressEndpoint, enabled, fetchConfigGET.headers, onEvent]);

  const disconnect = () => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
  };

  return { disconnect };
};
